import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CustomDate from "../../CustomForm/CustomDate";
import { useMutation } from "react-query";
import { createRecordAbsence } from "../../../api/services/recordAbsence";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import CustomInputSelect from "../../FormFields/CustomSelect/CustomInputSelect";
import CustomInput from "../../FormFields/CustomInput";
import CustomTimePicker from "../../FormFields/TiimePicker/TimePicker";

const RecordAbsencesModal = ({ show, onHide, refetchRecord }) => {
  const { id: clientId } = useParams();
  const [formState, setFormState] = useState({});

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const duration = useMemo(() => {
    if (!formState.start_date || !formState.end_date) return null;

    const startDate = new Date(formState.start_date);
    const endDate = new Date(formState.end_date);

    let startTime = formState.start_time
      ? typeof formState.start_time === "string"
        ? formState.start_time
        : formState.start_time.format("HH:mm:ss")
      : "00:00:00";

    let endTime = formState.end_time
      ? typeof formState.end_time === "string"
        ? formState.end_time
        : formState.end_time.format("HH:mm:ss")
      : "23:59:59";

    const [sh, sm, ss] = startTime.split(":").map(Number);
    const [eh, em, es] = endTime.split(":").map(Number);

    const start = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      sh || 0,
      sm || 0,
      ss || 0
    );

    const end = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      eh || 0,
      em || 0,
      es || 0
    );

    if (end <= start) return null;

    const diffMs = end - start;
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    const remainingHours = totalHours % 24;
    const remainingMinutes = totalMinutes % 60;

    return {
      days: totalDays,
      hours: remainingHours,
      minutes: remainingMinutes,
      totalHours,
      totalMinutes,
      display: `${totalDays} day${totalDays !== 1 ? "s" : ""}, ${remainingHours} hour${
        remainingHours !== 1 ? "s" : ""
      }, ${remainingMinutes} min${remainingMinutes !== 1 ? "s" : ""}`,
    };
  }, [
    formState.start_date,
    formState.start_time,
    formState.end_date,
    formState.end_time,
  ]);

  const postMutation = useMutation(createRecordAbsence, {
    onSuccess: () => {
      enqueueSnackbar("Record Absence created successfully", {
        variant: "success",
      });
      refetchRecord();
      onHide();
    },
    onError: () => {
      enqueueSnackbar("Failed to create Record Absence", { variant: "error" });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const recordAbsenceData = {
      ...formState,
      client: clientId,
    };
    postMutation.mutate(recordAbsenceData);
  };

  const handleClose = () => {
    setFormState({});
    onHide();
  };

  const absenceOptions = [
    "Planned Absence",
    "Unplanned Absence",
    "Not Applicable",
  ];
  const reasonOptions = ["Sick", "Family Emergency", "Vacation", "Other"];

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <Box sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EFF8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventNoteIcon sx={{ color: "#2469F0", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add Record Absence
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Record a new absence for the client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <PlayArrowIcon sx={{ color: "#10B981", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#10B981" }}>
                  Start of Absence
                </Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: alpha("#10B981", 0.2) }} />
              </Box>
              <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                  <CustomDate
                    label="Start Date *"
                    name="start_date"
                    value={formState.start_date}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                  <CustomTimePicker
                    label="Start Time"
                    name="start_time"
                    value={formState.start_time}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <StopIcon sx={{ color: "#EF4444", fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#EF4444" }}>
                  End of Absence
                </Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: alpha("#EF4444", 0.2) }} />
              </Box>
              <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                  <CustomDate
                    label="End Date *"
                    name="end_date"
                    value={formState.end_date}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                  <CustomTimePicker
                    label="End Time"
                    name="end_time"
                    value={formState.end_time}
                    onChange={handleChange}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

            {duration && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  p: 1.5,
                  borderRadius: "10px",
                  bgcolor: alpha("#2469F0", 0.05),
                  border: `1px dashed ${alpha("#2469F0", 0.3)}`,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTimeIcon sx={{ color: "#2469F0", fontSize: 20 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                    Total Absence Duration
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {duration.days > 0 && (
                    <Chip
                      label={`${duration.days} day${duration.days !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: "#2469F0",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                  {duration.hours > 0 && (
                    <Chip
                      label={`${duration.hours} hr${duration.hours !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: alpha("#2469F0", 0.15),
                        color: "#2469F0",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                  {duration.minutes > 0 && (
                    <Chip
                      label={`${duration.minutes} min${duration.minutes !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: alpha("#2469F0", 0.1),
                        color: "#2469F0",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ opacity: 0.3 }} />

            <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
              <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                <CustomInputSelect
                  name="reason"
                  label="Reason"
                  selectedValue={formState.reason}
                  onChange={handleChange}
                  options={reasonOptions}
                  initialLabel
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: "1 1 45%", minWidth: "200px" }}>
                <CustomInputSelect
                  name="absence_type"
                  label="Absence Type *"
                  selectedValue={formState.absence_type}
                  onChange={handleChange}
                  options={absenceOptions}
                  initialLabel
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <CustomInput
                label="Notes"
                name="note_message"
                value={formState.note_message}
                multiline
                rows={3}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <Box>
              <CustomInput
                label="Contact Information"
                name="contact_information"
                multiline
                rows={2}
                value={formState.contact_information}
                onChange={handleChange}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={postMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={postMutation.isLoading}
            startIcon={postMutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 3,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {postMutation.isLoading ? "Saving..." : "Save Absence"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecordAbsencesModal;